import React from 'react';
import { Card, CardHeader, CardBody, Badge, Button } from 'reactstrap';
import './BPAModule.css';

interface BPAModuleProps {
  id?: number;
  isCompact?: boolean;
}

/**
 * Best Practice Assessment Module component
 * Displays information about the BPA module
 * Can be displayed in compact or full view
 */
const BPAGuardFallback: React.FC<BPAModuleProps> = ({
  id,
  isCompact = false,
}) => {
  return isCompact ? <CompactView id={id} /> : <FullView id={id} />;
};

const CompactView: React.FC<{ id?: number }> = ({ id }) => (
  <Card className="bpa-module-compact">
    <CardHeader>
      <div className="d-flex align-items-center">
        <h4 className="mb-0 text-white mr-3">
          Best Practice Assessment Module
        </h4>
        <Badge className="premium-tag">Add-on</Badge>
      </div>
    </CardHeader>

    <CardBody>
      <p className="mb-2 mt-2">
        The Best Practice Assessment (BPA) module (an add-on paid feature) helps
        organizations evaluate their security posture against
        industry-recognized best practices. Ensure your infrastructure follows
        recommended security configurations and hardening guidelines from{' '}
        <strong>CIS Benchmarks</strong>, <strong>NIST</strong>,{' '}
        <strong>AWS Security Best Practices</strong>, and other leading
        frameworks.
      </p>

      <ul className="list-unstyled mb-3">
        <li className="mb-2">
          <i className="fa fa-check text-success mr-2"></i>
          <span>
            Align with <strong>CIS Benchmarks</strong>, <strong>NIST</strong>,
            and cloud-specific best practices
          </span>
        </li>
        <li className="mb-2">
          <i className="fa fa-check text-success mr-2"></i>
          <span>
            Evaluate cloud accounts, servers, and infrastructure configurations
          </span>
        </li>
        <li className="mb-2">
          <i className="fa fa-check text-success mr-2"></i>
          <span>
            Get actionable recommendations to improve security posture
          </span>
        </li>
      </ul>
      <div className="d-flex justify-content-between align-items-center mt-3">
        <Button
          color="primary"
          tag="a"
          role="link"
          href="mailto:support@securitypoint360.com"
        >
          <i className="fa fa-envelope mr-2"></i> Contact Us
        </Button>
        {id && <div className="text-muted small">Account ID: {id}</div>}
      </div>
    </CardBody>
  </Card>
);

const FullView: React.FC<{ id?: number }> = ({ id }) => (
  <Card>
    <CardHeader className="bg-primary text-white">
      <div className="d-flex align-items-center">
        <h3 className="mb-0 mr-3">Best Practice Assessment Module</h3>
        <Badge color="light" className="text-dark">
          Add-on
        </Badge>
      </div>
    </CardHeader>

    <CardBody>
      <p className="mb-4 mt-2 w-50">
        The Best Practice Assessment (BPA) module (an add-on paid feature) helps
        organizations evaluate their security posture against
        industry-recognized best practices. Ensure your infrastructure follows
        recommended security configurations and hardening guidelines from{' '}
        <strong>CIS Benchmarks</strong>, <strong>NIST</strong>,{' '}
        <strong>AWS Security Best Practices</strong>,{' '}
        <strong>Azure Security Baseline</strong>, and other leading frameworks.
      </p>

      <h4 className="mb-3">Key Benefits:</h4>

      <div className="d-flex align-items-start mb-3">
        <div className="mr-3 text-primary">
          <i className="fa fa-clipboard-check fa-lg"></i>
        </div>
        <div>
          <h6>Best Practice Alignment</h6>
          <p className="text-muted">
            Evaluate security configurations against{' '}
            <strong>CIS Benchmarks</strong>,{' '}
            <strong>NIST Security Framework</strong>, cloud provider best
            practices (<strong>AWS</strong>, <strong>Azure</strong>,{' '}
            <strong>GCP</strong>), and industry-leading security standards.
          </p>
        </div>
      </div>

      <div className="d-flex align-items-start mb-3">
        <div className="mr-3 text-primary">
          <i className="fa fa-cloud fa-lg"></i>
        </div>
        <div>
          <h6>Multi-Cloud and Hybrid Assessment</h6>
          <p className="text-muted">
            The module evaluates cloud accounts, virtual machines, containers,
            and on-premise infrastructure to provide comprehensive security
            posture visibility across your entire environment.
          </p>
        </div>
      </div>

      <div className="d-flex align-items-start mb-3">
        <div className="mr-3 text-primary">
          <i className="fa fa-lightbulb fa-lg"></i>
        </div>
        <div>
          <h6>Actionable Recommendations</h6>
          <p className="text-muted">
            Receive specific, prioritized recommendations to remediate
            misconfigurations and strengthen your security posture based on
            severity and potential impact.
          </p>
        </div>
      </div>

      <hr className="my-4" />

      <div className="d-flex justify-content-between align-items-center">
        <Button
          color="primary"
          role="link"
          tag="a"
          href="mailto:support@securitypoint360.com"
        >
          <i className="fa fa-envelope mr-2"></i> Contact Us
        </Button>
        <div className="text-muted small">
          {id && <span>Account ID: {id}</span>}
        </div>
      </div>
    </CardBody>
  </Card>
);

export default BPAGuardFallback;
